import { useMutation, useQuery } from '@apollo/client'
import { Button, Typography } from '@material-ui/core'
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace'
import { matchSorter } from 'match-sorter'
import { useEffect } from 'react'
import React, { useState } from 'react'

import ChannelDetailsContainer from '../../components/ChannelDetailsContainer/ChannelDetailsContainer'
import { DELETE_CART_MUTATION } from '../../graphql/cartCrud'
import { CHANNELS_WITH_LOGO_QUERY } from '../../graphql/channel'
import { cleanCartData, getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { Channel } from '../../types/channel'
import ChannelContainerSkeleton from './ChannelContainerSkeleton'
import ChannelHeader from './ChannelHeader'
import useStyles from './styles/index.style'

const SelectChannel: React.FC = () => {
  const classes = useStyles()
  const [searchInputValue, setSearchInputValue] = useState<string>('')
  const [selectedChannelType, setSelectedChannelType] = useState<string>('All')
  const fixedCount = 12
  const [count, setCount] = useState(fixedCount)
  const [filteredChannel, setFilteredChannel] = useState<Channel[]>([])

  const { data: channelData, loading: channelListLoading } = useQuery<{
    channelsWithLogo: Channel[]
  }>(CHANNELS_WITH_LOGO_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      if (getRoleFromStorage() === ROLE.ACCOMMODATION_OPERATOR) {
        setSelectedChannelType('HotelReservationOffice')
        setFilteredChannel(
          res.channelsWithLogo.filter(
            (item) => item.basicDetails.channelType === 'HotelReservationOffice'
          )
        )
      }
    }
  })

  useEffect(() => {
    const fullChannelList = channelData?.channelsWithLogo ?? []

    // First apply search
    let filtered = searchInputValue
      ? matchSorter(fullChannelList, searchInputValue, {
          keys: [
            'basicDetails.name',
            'basicDetails.name2',
            {
              key: 'basicDetails.code',
              threshold: matchSorter.rankings.STARTS_WITH
            }
          ],
          sorter: (rankedItems) =>
            rankedItems.sort((a, b) =>
              a.item.basicDetails.name > b.item.basicDetails.name ? 1 : -1
            )
        })
      : fullChannelList

    // Then apply channel type filter
    if (selectedChannelType !== 'All') {
      filtered = filtered.filter((item) =>
        item.basicDetails.channelType?.includes(selectedChannelType)
      )
    }

    if (filtered.length > fixedCount) {
      setCount(fixedCount)
    }

    setFilteredChannel(filtered)
  }, [searchInputValue, selectedChannelType, channelListLoading])

  const loadMoreOnClick = () => {
    if (filteredChannel.length >= count + fixedCount) {
      setCount(count + fixedCount)
    } else {
      const temp = filteredChannel.length % count
      setCount(count + temp)
    }
  }
  //DELETE_CART_MUTATION
  const [deleteCart] = useMutation(DELETE_CART_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      cleanCartData()
      localStorage.removeItem('confirmPrimitiveInput')
      localStorage.removeItem('confirmPrimitiveOutput')
      localStorage.removeItem('hotelDetails')
      localStorage.removeItem('primitiveData')
    },
    onError: () => {
      cleanCartData()
      localStorage.removeItem('confirmPrimitiveInput')
      localStorage.removeItem('confirmPrimitiveOutput')
      localStorage.removeItem('hotelDetails')
      localStorage.removeItem('primitiveData')
    }
  })

  useEffect(() => {
    if (localStorage.getItem('cartID')) {
      deleteCart({ variables: { cartID: localStorage.getItem('cartID') } })
    }
  }, [])

  return (
    <div className={classes.root}>
      <ChannelHeader
        searchInputValue={searchInputValue}
        setSearchInputValue={setSearchInputValue}
        selectedChannelType={selectedChannelType}
        setSelectedChannelType={setSelectedChannelType}
      />
      <div className={classes.grid}>
        {channelListLoading
          ? Array(12)
              .fill(null)
              .map((item, index) => (
                <ChannelContainerSkeleton key={index.toString()} />
              ))
          : filteredChannel
              .slice(0, count)
              .map((channel, index) => (
                <ChannelDetailsContainer key={index} data={channel} />
              ))}
      </div>
      <div className={classes.footer}>
        {count < filteredChannel.length && (
          <Button
            variant="outlined"
            color="primary"
            endIcon={
              <KeyboardBackspaceIcon
                className={lang === 'en' ? classes.icon : undefined}
              />
            }
            onClick={loadMoreOnClick}>
            <Typography variant="h4" className={classes.buttonText}>
              {getTranslate('Load More')}
            </Typography>
          </Button>
        )}
      </div>
    </div>
  )
}
export default SelectChannel
